import * as React from 'react'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Section Heading
 *
 * A distinctive heading component for page sections and card headers.
 * Uses Trajan Pro with uppercase tracking and an optional gold divider.
 *
 * This is the primary way to render branded headings — it enforces
 * the classical typographic conventions of the academy identity.
 *
 * Usage:
 *   <SectionHeading>Lunch Orders</SectionHeading>
 *   <SectionHeading subtitle="Week of March 28" withDivider>Menu</SectionHeading>
 *   <SectionHeading as="h2" size="lg">Time Off Requests</SectionHeading>
 */

type HeadingSize = 'sm' | 'md' | 'lg'

const sizeStyles: Record<HeadingSize, string> = {
	sm: 'text-h3 tracking-trajan-tight',
	md: 'text-h2 tracking-trajan',
	lg: 'text-h1 tracking-trajan-wide',
}

export interface SectionHeadingProps {
	children: React.ReactNode
	/** HTML element to render (defaults to h2) */
	as?: 'h1' | 'h2' | 'h3'
	/** Size variant */
	size?: HeadingSize
	/** Optional subtitle in body font */
	subtitle?: string
	/** Show a gold divider line below */
	withDivider?: boolean
	/** Additional classes */
	className?: string
}

export function SectionHeading({
	children,
	as: Tag = 'h2',
	size = 'md',
	subtitle,
	withDivider = false,
	className,
}: SectionHeadingProps) {
	return (
		<div className={cn('space-y-2', className)}>
			<div className="space-y-1">
				<Tag
					className={cn(
						'font-display uppercase font-bold text-navy leading-none',
						sizeStyles[size],
					)}
				>
					{children}
				</Tag>
				{subtitle && (
					<p className="text-body-sm text-muted-foreground font-body mt-1">
						{subtitle}
					</p>
				)}
			</div>
			{withDivider && <div className="divider-gold" />}
		</div>
	)
}
